import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { fetchSessionById } from '../features/sessions/slice-extensions/sessions-slice-extension';
import {
  selectSessions,
  selectSessionsStatus,
  selectSessionsStatusMessage,
  selectSessionsLastError,
} from '../features/sessions/sessions-slice';
import { LoadingStatus } from '../features/sessions/models/LoadingStatus';
import { InviteeType } from '../features/sessions/models/InviteeType';
import { BookingStatus } from '../features/sessions/models/BookingStatus';
import { RecurrenceType } from '../features/sessions/models/RecurrenceType';
import { ROUTES } from '../routes';
import SessionParticipants, { type SessionParticipant } from '../components/SessionParticipants';
import LeftMenu from '../components/LeftMenu';
import Breadcrumbs from '../components/Breadcrumbs';
import {
  mapBookingStatusToDisplay,
  mapPaymentActivityTypeToDisplay,
  mapRecurrenceTypeToDisplay,
  formatTimeDisplay,
  calculateEndTimeDisplay,
  formatDateDisplay,
  formatDurationDisplay
} from '../features/sessions/utils/DisplayMappers';

const SessionDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  
  const entities = useAppSelector(selectSessions);
  const status = useAppSelector(selectSessionsStatus);
  const statusMessage = useAppSelector(selectSessionsStatusMessage);
  const lastError = useAppSelector(selectSessionsLastError);
  const [redirectCountdown, setRedirectCountdown] = useState<number | null>(null);
  
  // Find the session in the store first, or fetch it if not available
  const session = entities.find((s) => s.id === id);
  
  // Check if this is a 404 error (session not found)
  const isSessionNotFound = status === LoadingStatus.Failed && 
    (lastError?.statusCode === 404 ||
     statusMessage?.toLowerCase().includes('not found') || 
     statusMessage?.toLowerCase().includes('booking not found') ||
     statusMessage?.toLowerCase().includes('session not found'));
  
  useEffect(() => {
    if (id && !session) {
      dispatch(fetchSessionById(id));
    }
  }, [id, session, dispatch]);

  // Handle 404 error with countdown and redirect
  useEffect(() => {
    if (isSessionNotFound) {
      setRedirectCountdown(3);
      
      const countdownInterval = setInterval(() => {
        setRedirectCountdown((prev) => {
          if (prev === null || prev <= 1) {
            clearInterval(countdownInterval);
            navigate(ROUTES.SESSIONS);
            return null;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownInterval);
    }
  }, [isSessionNotFound, navigate]);

  

  if (status === LoadingStatus.Loading) {
    return (
      <section>
        <div className="flex h-full gap-4">
          <LeftMenu />
          <div className="w-full p-4 pb-10 lg:px-10">
            <div className="lg:max-w-[694px]">
              <Breadcrumbs />
              <div className="space-y-4 py-6 lg:space-y-6">
                <div className="text-center py-4">
                  <p>Loading session data...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (status === LoadingStatus.Failed) {
    // Handle 404 error specifically
    if (isSessionNotFound) {
      return (
        <section>
          <div className="flex h-full gap-4">
            <LeftMenu />
            <div className="w-full p-4 pb-10 lg:px-10">
              <div className="lg:max-w-[694px]">
                <Breadcrumbs />
                <div className="space-y-4 py-6 lg:space-y-6">
                  <h1>Session Not Found</h1>
                  <div className="with-border rounded-2xl p-4 lg:p-6">
                    <p className="text-red-600 dark:text-red-400 mb-4">Session not found</p>
                    {redirectCountdown !== null && (
                      <p className="text-grey-30 dark:text-grey-70 mb-4">
                        Redirecting to Sessions List in {redirectCountdown} second{redirectCountdown !== 1 ? 's' : ''}...
                      </p>
                    )}
                    <div className="mt-4">
                      <button
                        onClick={() => navigate(ROUTES.SESSIONS)}
                        className="btn btn-secondary"
                      >
                        Go to Sessions List Now
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      );
    }

    // Handle other errors
    return (
      <section>
        <div className="flex h-full gap-4">
          <LeftMenu />
          <div className="w-full p-4 pb-10 lg:px-10">
            <div className="lg:max-w-[694px]">
              <Breadcrumbs />
              <div className="space-y-4 py-6 lg:space-y-6">
                <h1>Error Loading Session</h1>
                <div className="with-border rounded-2xl p-4 lg:p-6">
                  <p className="text-red-600 dark:text-red-400">{statusMessage || 'Failed to load session details'}</p>
                  <div className="mt-4 space-x-3">
                    <button
                      onClick={() => navigate(ROUTES.SESSIONS)}
                      className="btn btn-secondary"
                    >
                      Back to Sessions
                    </button>
                    <button
                      onClick={() => id && dispatch(fetchSessionById(id))}
                      className="btn btn-primary"
                    >
                      Retry
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!session) {
    return (
      <section>
        <div className="flex h-full gap-4">
          <LeftMenu />
          <div className="w-full p-4 pb-10 lg:px-10">
            <div className="lg:max-w-[694px]">
              <Breadcrumbs />
              <div className="space-y-4 py-6 lg:space-y-6">
                <h1>Session Not Found</h1>
                <div className="with-border rounded-2xl p-4 lg:p-6">
                  <p>The requested session could not be found.</p>
                  <div className="mt-4">
                    <button
                      onClick={() => navigate(ROUTES.SESSIONS)}
                      className="btn btn-secondary"
                    >
                      Back to Sessions
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Map invitees to participants for display
  const participants: SessionParticipant[] = session.invitees.map(invitee => ({
    name: `User ${invitee.invitedUserId.substring(0, 8)}`,
    type: invitee.type === InviteeType.Group ? 'group' as const : 'individual' as const,
    count: invitee.type === InviteeType.Group ? 1 : undefined,
  }));

  return (
    <section>
      <div className="flex h-full gap-4">
        <LeftMenu />
        <div className="w-full p-4 pb-10 lg:px-10">
          <div className="lg:max-w-[694px]">
            <Breadcrumbs />
            
            <div className="space-y-4 py-6 lg:space-y-6">
              {/* Header - Mobile: Session details at top, buttons underneath */}
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-center gap-2">
                  <h1>Session Details</h1>
                </div>
                <div className="flex flex-row items-center gap-3 w-full lg:w-auto">
                  <Link
                    to={ROUTES.SESSIONS}
                    className="btn btn-secondary flex-1 lg:flex-none lg:w-auto flex items-center justify-center gap-2"
                  >
                    <svg
                      width={20}
                      height={20}
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"
                        fill="#4D4D4D"
                      />
                    </svg>
                    Back to List
                  </Link>
                  <Link
                    to={ROUTES.EDIT_SESSION.replace(':id', session.id)}
                    state={{ from: location.pathname }}
                    className="btn btn-secondary flex-1 lg:flex-none lg:min-w-[120px] flex items-center justify-center gap-2"
                  >
                    <svg
                      width={20}
                      height={20}
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
                        fill="#4D4D4D"
                      />
                    </svg>
                    Edit
                  </Link>
                </div>
              </div>

              {/* Session Title Card */}
              <div className="with-border rounded-2xl p-4 lg:p-6">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <svg
                      className="lg:size-12"
                      fill="none"
                      height="48"
                      viewBox="0 0 48 48"
                      width="48"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect x="6" y="8" width="36" height="32" rx="4" stroke="#808080" strokeWidth="2" fill="none"/>
                      <line x1="6" y1="16" x2="42" y2="16" stroke="#808080" strokeWidth="2"/>
                      <line x1="14" y1="4" x2="14" y2="12" stroke="#808080" strokeWidth="2" strokeLinecap="round"/>
                      <line x1="34" y1="4" x2="34" y2="12" stroke="#808080" strokeWidth="2" strokeLinecap="round"/>
                      <circle cx="16" cy="24" r="1.5" fill="#808080"/>
                      <circle cx="24" cy="24" r="1.5" fill="#808080"/>
                      <circle cx="32" cy="24" r="1.5" fill="#808080"/>
                      <circle cx="16" cy="32" r="1.5" fill="#808080"/>
                      <circle cx="24" cy="32" r="1.5" fill="#808080"/>
                    </svg>
                    <h2>{session.title}</h2>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${session.status === BookingStatus.Active ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300'}`}>
                    {mapBookingStatusToDisplay(session.status)}
                  </div>
                </div>

                <div className="mt-4 space-y-4 lg:mt-6 lg:space-y-6">
                  {/* Date */}
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-grey-95 dark:bg-primary-90 shrink-0">
                      <svg
                        className="w-6 h-6 fill-grey-30 dark:fill-primary-40"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-grey-30 dark:text-grey-70 text-sm">Date</p>
                      <p className="font-medium text-grey-10 dark:text-grey-90">{formatDateDisplay(session.dateStart)}</p>
                    </div>
                  </div>

                  {/* Time */}
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-grey-95 dark:bg-primary-90 shrink-0">
                      <svg
                        className="w-6 h-6 fill-grey-30 dark:fill-primary-40"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm4.2 14.2L11 13V7h1.5v5.2l4.5 2.7-.8 1.3z"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-grey-30 dark:text-grey-70 text-sm">Time</p>
                      <p className="font-medium text-grey-10 dark:text-grey-90">
                        {formatTimeDisplay(session.timeLocal)} - {calculateEndTimeDisplay(session.timeLocal, session.duration)} ({formatDurationDisplay(session.duration)})
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Session Details Card */}
              <div className="with-border rounded-2xl p-4 lg:p-6">
                <h3 className="text-grey-10 dark:text-grey-90 mb-4 lg:mb-6">Session Information</h3>
                <div className="space-y-4 lg:space-y-6">

                  {/* Time Zone */}
                  {!session.isAllDay && (
                    <div className="flex items-center gap-4">
                      <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-grey-95 dark:bg-primary-90 shrink-0">
                        <svg
                          className="w-6 h-6 fill-grey-30 dark:fill-primary-40"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-.61.08-1.21.21-1.78L9.99 16v1c0 1.1.9 2 2 2v1.93C7.06 20.44 4 16.63 4 12zm13.89 5.4c-.26-.81-1-1.4-1.89-1.4h-1v-3c0-.55-.45-1-1-1h-6v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41C17.92 5.77 20 8.65 20 12c0 2.08-.81 3.98-2.11 5.4z"/>
                        </svg>
                      </div>
                      <div className="flex-1">
                        <p className="text-grey-30 dark:text-grey-70 text-sm">Time Zone</p>
                        <p className="font-medium text-grey-10 dark:text-grey-90">{session.timeZone}</p>
                      </div>
                    </div>
                  )}

                  {/* Payment Type */}
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-grey-95 dark:bg-primary-90 shrink-0">
                      <svg
                        className="w-6 h-6 fill-grey-30 dark:fill-primary-40"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-grey-30 dark:text-grey-70 text-sm">Payment Type</p>
                      <p className="font-medium text-grey-10 dark:text-grey-90">{mapPaymentActivityTypeToDisplay(session.paymentActivityType)}</p>
                    </div>
                  </div>

                  {/* Recurrence */}
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-grey-95 dark:bg-primary-90 shrink-0">
                      <svg
                        className="w-6 h-6 fill-grey-30 dark:fill-primary-40"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M12 6v3l4-4-4-4v3c-4.42 0-8 3.58-8 8 0 1.57.46 3.03 1.24 4.26L6.7 14.8c-.45-.83-.7-1.79-.7-2.8 0-3.31 2.69-6 6-6zm6.76 1.74L17.3 9.2c.44.84.7 1.79.7 2.8 0 3.31-2.69 6-6 6v-3l-4 4 4 4v-3c4.42 0 8-3.58 8-8 0-1.57-.46-3.03-1.24-4.26z"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-grey-30 dark:text-grey-70 text-sm">Recurrence</p>
                      <p className="font-medium text-grey-10 dark:text-grey-90">{mapRecurrenceTypeToDisplay(session.recurrenceType)}</p>
                    </div>
                  </div>

                  {/* Description */}
                  {session.description && (
                    <div className="flex items-start gap-4">
                      <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-grey-95 dark:bg-primary-90 shrink-0 mt-0">
                        <svg
                          className="w-6 h-6 fill-grey-30 dark:fill-primary-40"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                        </svg>
                      </div>
                      <div className="flex-1">
                        <p className="text-grey-30 dark:text-grey-70 text-sm">Description</p>
                        <p className="font-medium text-grey-10 dark:text-grey-90">{session.description}</p>
                      </div>
                    </div>
                  )}

                  {/* Reminder */}
                  {session.remindBefore && (
                    <div className="flex items-center gap-4">
                      <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-grey-95 dark:bg-primary-90 shrink-0">
                        <svg
                          className="w-6 h-6 fill-grey-30 dark:fill-primary-40"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
                        </svg>
                      </div>
                      <div className="flex-1">
                        <p className="text-grey-30 dark:text-grey-70 text-sm">Reminder</p>
                        <p className="font-medium text-grey-10 dark:text-grey-90">{formatDurationDisplay(session.remindBefore)} before</p>
                      </div>
                    </div>
                  )}

                </div>
              </div>

              {/* Participants Card */}
              {participants.length > 0 && (
                <div className="with-border rounded-2xl p-4 lg:p-6">
                  <h3>Participants</h3>
                  <div className="mt-4">
                    <SessionParticipants participants={participants} />
                  </div>
                </div>
              )}

              {/* Recurrence Details Card */}
              {session.recurrenceType !== RecurrenceType.No && (
                <div className="with-border rounded-2xl p-4 lg:p-6">
                  <h3>Recurrence Details</h3>
                  <div className="mt-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-grey-30 dark:text-grey-70">Pattern:</span>
                      <span className="font-medium">{mapRecurrenceTypeToDisplay(session.recurrenceType)}</span>
                    </div>
                    {session.recurrenceDays && session.recurrenceDays.length > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-grey-30 dark:text-grey-70">Days:</span>
                        <span className="font-medium">{session.recurrenceDays.join(', ')}</span>
                      </div>
                    )}
                    {session.recurrenceDateEnd && (
                      <div className="flex justify-between items-center">
                        <span className="text-grey-30 dark:text-grey-70">End Date:</span>
                        <span className="font-medium">{formatDateDisplay(session.recurrenceDateEnd)}</span>
                      </div>
                    )}
                    {session.finishAfterOccurrences && (
                      <div className="flex justify-between items-center">
                        <span className="text-grey-30 dark:text-grey-70">Occurrences:</span>
                        <span className="font-medium">{session.finishAfterOccurrences}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SessionDetailsPage;